import React from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useTranslation } from 'react-i18next'
import { useZtSubsetDataContext } from '../context'
import DataSkeleton from '../dataSkeleton'
import { formatEmpty } from '../utils'

echarts.registerTheme('theme_jekka', {
	backgroundColor: '#fff'
})

const ChartFour: React.FC = () => {
	const { t } = useTranslation()
	const { infoData, chartLoading, filters } = useZtSubsetDataContext()
	const { outbound_scene } = filters

	/** 是否异常件外呼 */
	const isExceptionScene = outbound_scene == 'delivery_fail'

	const connectionPairs = (infoData?.list || []).map((item: any) => ({
		area: item.area,
		count: Number(item.connection_count),
		rate: item.connection_rate,
		returnCount: Number(item.connection_return_count || 0),
		redispatchCount: Number(item.connection_redispatch_count || 0)
	}))

	connectionPairs?.sort((a: any, b: any) => (b.count || 0) - (a.count || 0))

	const areas = connectionPairs?.map((item: any) => item.area)
	const ConnectionList = connectionPairs?.map((item: any) => item.count || 0)
	const ConnectionRate = connectionPairs?.map((item: any) =>
		item.rate !== null && item.rate !== undefined ? item.rate : 0
	)
	const ConnectionReturnList = connectionPairs?.map((item: any) => item.returnCount)
	const ConnectionRedispatchList = connectionPairs?.map((item: any) => item.redispatchCount)

	const optionList = {
		tooltip: {
			trigger: 'axis',
			confine: true,
			extraCssText: 'max-width: 450px; white-space: normal;',
			formatter: (params: any) => {
				if (!params || params.length === 0) return ''
				const idx = params[0].dataIndex

				const lines: string[] = []

				if (isExceptionScene) {
					lines.push(
						`<div style="margin-bottom:8px;font-weight:bold;">${
							isExceptionScene
								? t('c_a_outbound_call_effective_rate_tips')
								: t('c_a_outbound_call_feedback')
						}</div>`
					)

					params.forEach((item: any) => {
						const name = areas[idx] ?? ''
						const value =
							item.seriesType === 'line' ? `${ConnectionRate[idx] ?? 0}%` : ConnectionList[idx] ?? 0

						lines.push(
							`<div style="margin-bottom:6px; display:flex; align-items:center;">` +
								`<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${item.color};margin-right:6px;"></span>` +
								`<span>${item.seriesName}</span>` +
								`<span>(${t(`c_a_regional_map_${name}`)})：</span>` +
								`<span style="font-weight:bold;">${value}</span>` +
								`</div>`
						)
					})
				} else {
					const connectionCount = ConnectionList[idx] ?? 0
					lines.push(`
						<div style="margin-bottom:6px; display:flex; align-items:center;">
							<span>${t('c_a_outbound_call_effective_quantity')}：</span>
							<span style="font-weight:bold;">${connectionCount}</span>
						</div>
					`)

					params.forEach((item: any) => {
						lines.push(`
							<div style="margin-bottom:6px; display:flex; align-items:center;">
								<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${
									item.color
								};margin-right:6px;"></span>
								<span>${item.seriesName}：</span>
								<span style="font-weight:bold;">${item.value ?? 0}</span>
							</div>
						`)
					})
				}

				return lines.join('')
			}
		},
		legend: null,
		grid: {
			left: 10,
			right: 10,
			bottom: 12,
			containLabel: true
		},

		yAxis: isExceptionScene
			? [
					{ type: 'value' },
					{
						type: 'value',
						axisLabel: { formatter: '{value}%' },
						splitLine: { show: false }
					}
			  ]
			: [{ type: 'value' }],
		xAxis: {
			type: 'category',
			data: areas?.map((area: any) => t(`c_a_regional_map_${area}`))
		},

		series: isExceptionScene
			? [
					/** 有效客户接通量  */
					{
						name: t('c_a_outbound_call_effective_connection'),
						type: 'bar',
						data: ConnectionList,
						itemStyle: { color: '#ffb72b', barBorderRadius: [8, 8, 0, 0] },
						barWidth: ConnectionList?.length < 9 ? 36 : 'auto',
						label: {
							show: true,
							offset: [0, -10],
							formatter: (params: any) => formatEmpty(params.value)
						},
						smooth: true
					},
					/** 有效客户接通率  */
					{
						name: t('c_a_outbound_call_effective_rate'),
						type: 'line',
						yAxisIndex: 1,
						itemStyle: { color: '#8B5CF6' },
						data: ConnectionRate,
						label: { show: true, position: 'top', formatter: '{c}%' },
						smooth: true
					}
			  ]
			: [
					/** 已收到反馈 */
					{
						name: t('c_a_outbound_call_received'),
						type: 'bar',
						data: ConnectionReturnList.map((item: number, idx: number) => ({
							value: item,
							itemStyle: {
								color: '#6CE565',
								borderRadius: (ConnectionRedispatchList[idx] ?? 0) > 0 ? 0 : [6, 6, 0, 0]
							}
						})),
						label: {
							show: ConnectionReturnList?.length < 18,
							color: '#fff',
							formatter: (params: any) => formatEmpty(params.value)
						},
						stack: 'resolve',
						barWidth: ConnectionReturnList?.length < 9 ? 36 : 'auto'
					},
					/** 未收到反馈 */
					{
						name: t('c_a_outbound_call_not_received'),
						type: 'bar',
						data: ConnectionRedispatchList.map((item: number) => item),
						itemStyle: { color: '#ffb72b', borderRadius: [6, 6, 0, 0] },
						label: {
							show: ConnectionRedispatchList?.length < 18,
							formatter: (params: any) => formatEmpty(params.value)
						},
						stack: 'resolve',
						barWidth: ConnectionRedispatchList?.length < 9 ? 36 : 'auto'
					}
			  ]
	}

	const rate = infoData?.total?.connection_rate
	const rateValue = Number.isFinite(Number(rate)) ? Number(rate).toFixed(2) : undefined

	return (
		<div className="bg-white rounded-lg overflow-hidden px-4 w-[49.5%]">
			<div className="mt-[18px]">
				<div className="text-[14px] font-medium mb-[12px]">
					{t('c_a_outbound_call_effective_connection')}
				</div>
				<div className="text-[14px] mb-[8px]">
					{isExceptionScene
						? t('c_a_outbound_call_effective_rate_tips')
						: t('c_a_outbound_call_feedback')}
				</div>
				<div className="text-[18px] font-medium flex items-center">
					<div className={isExceptionScene ? 'mr-[40px]' : ''}>
						<DataSkeleton value={infoData?.total?.connection_count} />
					</div>
					{isExceptionScene && <DataSkeleton value={rateValue} showPercent />}
				</div>
			</div>

			<ReactECharts theme="theme_jekka" option={optionList} showLoading={chartLoading} />
		</div>
	)
}

export default ChartFour
